import { MoreVertical, Pencil, Trash2 } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
  type DataTablePagination,
  EmptyState,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
} from '@atrvd/saas-ui'
import type { Category } from '@/lib/supabase'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'

/**
 * Props for CategoriesTable component
 */
interface CategoriesTableProps extends TableQueryStateProps {
  categories: Category[]
  onEdit: (category: Category) => void
  onDelete: (category: Category) => void
  /** Footer config; omit (or pass `undefined`) to render no footer. */
  pagination?: DataTablePagination
}

/**
 * Get status badge variant and label using theme-aware Badge variants
 */
function getStatusBadge(isActive: boolean) {
  if (isActive) {
    return { label: 'Active', variant: 'info' as const }
  }
  return { label: 'Inactive', variant: 'muted' as const }
}

/**
 * Table component for displaying categories
 */
export function CategoriesTable({
  categories,
  onEdit,
  onDelete,
  pagination,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
}: CategoriesTableProps) {
  /**
   * Column definitions for categories table
   */
  const columns: DataTableColumn<Category>[] = [
    {
      key: 'name',
      header: 'Category Name',
      cellClassName: 'font-medium',
      render: (category) => (
        <div>
          <p className="font-medium">{category.name}</p>
          {category.description && (
            <p className="text-sm text-muted-foreground">{category.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (category) => {
        const badge = getStatusBadge(category.is_active)
        return (
          <Badge variant={badge.variant}>
            {badge.label}
          </Badge>
        )
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (category) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-label="Open menu"
            >
              <MoreVertical className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(category)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(category)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, categories.length)

  return (
    <>
      {banner}
      <DataTable
        columns={columns}
        data={categories}
        rowKey={(category) => category.id}
        pagination={pagination}
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        itemName="categories"
        emptyState={
          <EmptyState
            icon={Pencil}
            title="No categories yet"
            description="Create your first category to organize transactions"
          />
        }
      />
    </>
  )
}
